<template>
  <section v-bind="$attrs" class="rounded-xl bg-gray-100 pb-8 ps-8 pt-8">
    <div class="mb-8 flex items-center justify-between pe-12 ps-4">
      <div class="text-subject font-bold text-dark">
        {{ `${$t('search.flight')} ${$t('results')}` }}
      </div>

      <JButton
        v-if="
          !isLoadingSearchAndFilter && !flightSearchResultsStore.isSearchEmpty
        "
        type="button"
        size="xs"
        color="secondary"
        icon="filter"
        variant="tonal"
        @click="!flightSearchResultsStore.isLoading && setFilterDialog(true)"
      />
    </div>

    <div
      v-if="
        !isLoadingSearchAndFilter &&
        flightSearchResultsStore.totalFlightCount > 0
      "
      class="my-12 flex items-center justify-between pe-12 ps-4"
    >
      <span class="font-bold">{{ $t('search.chooseYourFlight') }}</span>
      <span class="font-medium text-gray-600">
        {{
          $t('search.nResults', {
            count: flightSearchResultsStore.totalFlightCount,
          })
        }}
      </span>
    </div>

    <!-- CAROUSEL -->
    <div ref="swiperElementRef" class="swiper">
      <div class="swiper-wrapper">
        <template
          v-if="
            flightSearchResultsStore.flightsList.length ||
            isLoadingSearchAndFilter
          "
        >
          <template v-if="isLoadingSearchAndFilter">
            <div v-for="i in 4" :key="i" class="swiper-slide">
              <FlightCardSkeleton />
            </div>
          </template>

          <template v-else>
            <div
              v-for="(flight, index) in flightSearchResultsStore.flightsList"
              :key="index"
              class="swiper-slide"
            >
              <FlightCard
                :flight="flight"
                @select="onSelect(flight)"
                @delete="onDeselect(flight.uniqueId)"
                @show-detail="showFlightDetail(flight)"
              />
            </div>

            <template
              v-if="
                flightSearchResultsStore.flightsList.length !==
                flightSearchResultsStore.totalFlightCount
              "
            >
              <div
                v-for="i in MINIMUM_RESULT_SIZE"
                :key="i"
                class="swiper-slide"
              >
                <FlightCardSkeleton class="h-full" />
              </div>
            </template>
          </template>
        </template>

        <!-- NO RESULT -->
        <template v-else>
          <NoSearchResult
            :image="{ src: '/airplane.svg', alt: 'Flight' }"
            :description="
              $t('search.noResultFound', { title: $t('search.flight') })
            "
            class="w-full"
          />
        </template>
      </div>
    </div>
  </section>

  <!-- FILTER -->
  <JBottomSheet v-model="filterDialogIsOpen" hasPopstate>
    <template #header>
      <JContainer class="px-16 pt-24 text-pane-header font-bold text-dark">
        {{ $t('flight') }} {{ $t('words.filters') }}
      </JContainer>
    </template>

    <div class="p-24">
      <FlightResultsFilter @submit="onSubmitForm" />
    </div>
  </JBottomSheet>

  <!-- DETAIL -->
  <JBottomSheet v-model="flightDetailIsOpen" fullScreen hasPopstate>
    <template #header>
      <JContainer
        class="absolute left-0 top-0 z-10 flex w-full justify-center px-16"
      >
        <TheMobileHeader
          showBack
          :title="$t('search.flightDetails')"
          titleClass="font-bold text-base flex-[10] px-16"
          @back="setFlightDetailIsOpen(false)"
        />
      </JContainer>
    </template>

    <ServerError v-if="isError" />

    <FlightDetail
      v-else
      :isLoading="flightInfoIsFetching"
      @select="findFlightAndAddToItinerarySummary"
      @delete="onDeselect"
      @retry="refetch"
    />
  </JBottomSheet>
</template>

<script setup lang="ts">
import Swiper, { type Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import { useToggle } from '@vueuse/core'
import {
  computed,
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { JBottomSheet, JButton, JContainer } from '@journey/ui-kit'
import type { Flight } from '@/modules/Search/types/model/search'
import { useFlightInfoQuery } from '@/modules/Search/requests/queries'
import useItinerarySummary from '@/modules/Search/store/itinerarySummary'
import useFlightSearchResultsStore from '@/modules/Search/store/flightSearchResults'
import {
  FilteredFlightListSortBy,
  type SearchPanelFlightFilters,
} from '@/modules/Search/types'
import useFlightSearchFiltersStore from '@/modules/Search/store/flightSearchFilters'
import { useFilteredFlightListMutation } from '@/modules/Search/requests/mutations'
import type { FilteredFlightListPayload } from '@/modules/Search/types/api/payload/filteredFlightList'
import {
  DEFAULT_FLIGHT_PAGE_SIZE,
  MINIMUM_RESULT_SIZE,
} from '@/modules/Search/constants'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import { useItinerary } from '@/modules/Search/composables'
import useToastStore from '@/modules/Core/store/toast'
import FlightCard from '@/modules/Core/components/shared/FlightCard/FlightCard.vue'
import FlightCardSkeleton from '@/modules/Core/components/shared/FlightCard/FlightCardSkeleton/FlightCardSkeleton.vue'
import useAuthStore from '@/modules/Auth/store'
import featureFlag from '@/config/featureFlag'

defineOptions({
  inheritAttrs: false,
})

const FlightDetail = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/FlightDetail/FlightDetail.vue')
)
const ServerError = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/ServerError/ServerError.vue')
)
const TheMobileHeader = defineAsyncComponent(
  () =>
    import('@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue')
)
const NoSearchResult = defineAsyncComponent(
  () => import('@/modules/Search/components/NoSearchResult/NoSearchResult.vue')
)
const FlightResultsFilter = defineAsyncComponent(
  () =>
    import(
      '@/modules/Search/components/FlightResults/FlightResultsFilter/FlightResultsFilter.vue'
    )
)

const swiperRef = ref<SwiperType | null>(null)
const swiperElementRef = ref<HTMLElement | null>(null)

// FILTERS
const apiPage = ref(1)
const size = ref(DEFAULT_FLIGHT_PAGE_SIZE)
const query = ref('')

const flightSearchResultsStore = useFlightSearchResultsStore()
const authStore = useAuthStore()

onMounted(() => {
  swiperRef.value = new Swiper(swiperElementRef.value!, {
    slidesPerView: 1.3,
    spaceBetween: 10,
    on: {
      slideChange: onSlideChange,
    },
  })
})

const itinerarySummaryStore = useItinerarySummary()
const searchPanelHistoryStore = useSearchPanelHistoryStore()
const { submitItinerary } = useItinerary()

const [filterDialogIsOpen, setFilterDialog] = useToggle(false)
const [flightDetailIsOpen, setFlightDetailIsOpen] = useToggle(false)

const searchId = computed(
  () => searchPanelHistoryStore.history.searchId as string
)
const customerCode = ref(featureFlag.customerCode)
const searchOption = ref(-1)

const toastStore = useToastStore()
const { t } = useI18n()

async function onSelect(flight: Flight) {
  itinerarySummaryStore.select(flight)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  toastStore.addToast({
    title: t('search.itinerarySummary.addMessages.flightAdded'),
    color: 'success',
  })
}

async function onDeselect(uniqueId: string) {
  itinerarySummaryStore.remove(uniqueId)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)
}

const {
  data: flightDetail,
  isFetching: flightInfoIsFetching,
  isError,
  refetch,
} = useFlightInfoQuery(searchId, searchOption, customerCode, {
  enabled: flightDetailIsOpen,
})

function showFlightDetail(payload: Flight) {
  searchOption.value = payload.searchOption

  setFlightDetailIsOpen(true)
}

watch(flightDetail, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  flightSearchResultsStore.setSelectedFlight(value.data)
})

async function findFlightAndAddToItinerarySummary(id: string) {
  const flight = flightSearchResultsStore.findFlightById(id)

  if (!flight) {
    return
  }

  itinerarySummaryStore.select(flight)

  const { data } = await submitItinerary()

  itinerarySummaryStore.setItineraries(data.data.itinerary)

  if (!flightDetailIsOpen.value) {
    return
  }

  setFlightDetailIsOpen(false)
}

const { mutateAsync } = useFilteredFlightListMutation({
  meta: {
    noSuccessToast: true,
  },
})

const filteredFlightsStore = useFlightSearchFiltersStore()

const apiPayload = reactive<FilteredFlightListPayload>({
  searchId: '',
  sortBy: filteredFlightsStore.filters.sortBy,
  flightType: filteredFlightsStore.filters.flightType,
  stopFilter: filteredFlightsStore.filters.stopFilter,
  airlines: filteredFlightsStore.filters.airlines,
  items: filteredFlightsStore.filters.items,
  query: query.value,
  page: apiPage.value,
  size: size.value,
  sortOrder: 'ascending',
  residency: authStore.userResidency,
})

const [isLoadingFilter, toggleIsFilterLoading] = useToggle()

async function onSubmitFilters() {
  apiPayload.searchId = searchId.value

  const { data } = await mutateAsync(apiPayload, {
    onError: () => {
      if (apiPayload.page === 1) {
        flightSearchResultsStore.setTotalFlightCount(0)
        flightSearchResultsStore.setTotalFlightPages(0)
      }
      toggleIsFilterLoading(false)
    },
  })

  if (!data) {
    return
  }

  const { items, page } = data.data

  flightSearchResultsStore.appendFlights(items)

  if (page.items !== flightSearchResultsStore.totalFlightCount) {
    flightSearchResultsStore.setTotalFlightCount(page.items ?? 1)
  }
}

async function onSubmitForm(filters: SearchPanelFlightFilters) {
  swiperRef.value?.slideTo(0)

  toggleIsFilterLoading()

  apiPage.value = 1
  Object.assign(apiPayload, {
    sortBy: filters.sortBy ? filters.sortBy : FilteredFlightListSortBy.Disabled,
    stopFilter: filters.stopFilter,
    flightType: filters.flightType,
    airlines: filters.airlines,
    items: filters.items,
    page: 1,
  } as FilteredFlightListPayload)

  setFilterDialog(false)

  flightSearchResultsStore.resetFlights()

  await onSubmitFilters()

  toggleIsFilterLoading()
}

watch(isLoadingFilter, filteredFlightsStore.setLoading)

const isLoadingSearchAndFilter = computed(
  () => flightSearchResultsStore.isLoading || isLoadingFilter.value
)

async function onSlideChange() {
  const activeIndex = swiperRef.value?.activeIndex
  if (activeIndex === undefined) {
    return
  }

  if (
    flightSearchResultsStore.flightsList.length ===
    flightSearchResultsStore.totalFlightCount
  ) {
    return
  }

  if ((activeIndex + 1) % (DEFAULT_FLIGHT_PAGE_SIZE - 4) !== 0) {
    return
  }

  if (
    Math.ceil((activeIndex + 1) / DEFAULT_FLIGHT_PAGE_SIZE) < apiPage.value &&
    apiPage.value !== 1
  ) {
    return
  }

  swiperRef.value?.update()

  await getFetchOrScrollFlightsStatus()

  swiperRef.value?.update()
}

async function getFetchOrScrollFlightsStatus() {
  if (isLoadingFilter.value) {
    return
  }

  apiPage.value++
  apiPayload.page = apiPage.value
  await onSubmitFilters()
}

async function onSelectCity(value?: string) {
  toggleIsFilterLoading(true)

  apiPayload.items = []

  if (value) {
    apiPayload.destinations = [value.toLocaleLowerCase()]
  } else {
    delete apiPayload.destinations
  }

  apiPage.value = 1
  apiPayload.page = apiPage.value

  flightSearchResultsStore.resetFlights()

  await onSubmitFilters()

  toggleIsFilterLoading(false)
}

watch(
  () => flightSearchResultsStore.isLoading,
  () => {
    apiPage.value = 1
  }
)

onBeforeUnmount(() => {
  swiperRef.value?.destroy()
})

defineExpose({
  onSelectCity,
})
</script>

<style lang="postcss" scoped>
.swiper {
  @apply z-0;
}
</style>
